/**
 * Brand Brain — the confirmed, versioned brand-knowledge layer.
 * Mirrors the Brand-Brain service's provenance model exactly: every leaf is a
 * Field carrying its source + evidence + status. Enums are exact contracts.
 */

export type BBSource =
  | 'brand_agent'
  | 'competitor_agent'
  | 'files'
  | 'user'
  | 'default'
  | 'learned';

export type BBStatus = 'draft' | 'confirmed' | 'edited' | 'default' | 'new' | 'learning';

export type BBEvidenceKind = 'url' | 'file' | 'post' | 'script_edit' | 'derived';

export interface BBEvidence {
  kind: BBEvidenceKind;
  ref: string;
  quote: string;
  /** When the source was observed; null/absent = legacy/unknown. */
  observed_at?: string | null;
  /** The page's <title> for url refs ("Plans and prices"); absent on legacy evidence. */
  title?: string | null;
}

/* ---- structured page data (derived by the run, never confirmable) ---- */

export interface BBSocial {
  platform: 'instagram' | 'youtube' | 'tiktok' | 'x' | 'facebook' | 'linkedin';
  handle: string;
  url: string;
  confirmed: boolean;
  /** The account's profile picture as the build's profile fetch returned it: an Instagram CDN link that expires in days, a display fallback only. */
  picture_url?: string | null;
}

/** A creator who posted for the brand — the structured row behind `past_creators`. */
export interface BBCreatorCard {
  handle: string;
  name: string;
  platform: 'instagram' | 'youtube' | 'tiktok' | 'facebook' | 'other';
  verified: boolean;
  /** nano | micro | macro | mega */
  size: string;
  followers: number | null;
  niche: string;
  /** confirmed | likely_paid | review | organic | ad */
  disclosure: string;
  did_best: boolean;
  post_url: string | null;
  thumbnail_url: string | null;
  views: number | null;
}

/** One of the brand's own recent posts. */
export interface BBPostCard {
  platform: 'instagram' | 'youtube' | 'tiktok';
  kind: 'reel' | 'video' | 'image' | 'carousel';
  url: string;
  /** Platform CDN link; may expire — hide a card whose image fails. */
  thumbnail_url: string | null;
  caption: string;
  views: number | null;
  likes: number;
  comments: number;
  posted_at: string | null;
  paid_partnership: boolean;
}

export interface BBPostMetrics {
  creators_posted: number;
  did_best: number;
  top_format: string | null;
  /** percent of the brand's own posts */
  top_format_share: number | null;
  main_platform: string | null;
  /** kind -> count */
  format_mix: Record<string, number>;
  window_days: number;
}

/** Computed at read time by the service (never stored, never LLM-emitted). */
export type BBConfidence = 'strong' | 'moderate' | 'weak' | 'unverified';
export type BBFreshness = 'fresh' | 'stale' | 'unknown';

export interface BBField {
  id: string;
  value: string;
  source: BBSource;
  evidence: BBEvidence[];
  status: BBStatus;
  /** fact | metric | inference | recommendation (absent on legacy docs). */
  kind?: 'fact' | 'metric' | 'inference' | 'recommendation';
  confidence?: BBConfidence;
  /** Omitted for user-policy families (rules/notes) — policy can't go stale. */
  freshness?: BBFreshness;
}

export interface BBRivalIntel {
  positioning: BBField | null;
  creators: BBField[];
  ads_summary: BBField | null;
  /** Display identity (the record key is the handle); absent on legacy drafts. */
  name?: string;
  domain?: string;
  logo_url?: string | null;
  /** Where a watched rival is on its way into the comparison (Brand-Brain #17); absent on older drafts. */
  state?: 'queued' | 'reading' | 'ready' | 'failed' | null;
  /** Plain words for the page when `state` is failed ("Instagram handle not found"). */
  failure?: string | null;
  /** The service's tries at bringing a queued rival in, and when the next may start (ISO). */
  attempts?: number;
  next_attempt_at?: string | null;
}

export interface BBCompetitorBlock {
  state: 'pending' | 'ready' | 'confirmed';
  what_you_post: BBField | null;
  worked_with: BBField | null;
  did_best: BBField | null;
  creator_type: BBField | null;
  content_that_worked: BBField | null;
  past_creators: BBField[];
  seed_opt_in: BBField | null;
  rivals: Record<string, BBRivalIntel>;
  /** Structured page data; absent/empty on legacy drafts. */
  creators?: BBCreatorCard[];
  posts?: BBPostCard[];
  metrics?: BBPostMetrics | null;
}

export interface BBTopics {
  brand: {
    products: BBField | null;
    what_you_do: BBField | null;
    differentiators: BBField | null;
    markets_sell: BBField | null;
    markets_campaign: BBField | null;
    /** Features included with every plan ("Built in"); absent on legacy drafts. */
    features?: BBField | null;
  };
  audience: {
    target: BBField | null;
    suggested_groups: BBField[];
    notes: BBField | null;
  };
  voice: {
    tone: BBField | null;
    key_messages: BBField | null;
  };
  rules: {
    audience_must_haves: BBField | null;
    rival_deal: BBField | null;
    topics_avoid: BBField | null;
    never_work_with: BBField | null;
    rival_blocklist: BBField | null;
    custom: BBField[];
  };
  reputation: Record<string, BBField>;
}

export interface BBLegal {
  disclosure: BBField | null;
  approved_claims: BBField | null;
  never_claim: BBField | null;
  never_show_say: BBField | null;
}

export interface BrandBrain {
  brand_id: string;
  version: number;
  status: 'draft' | 'confirmed';
  rev: number;
  topics: BBTopics;
  legal: BBLegal;
  competitor: BBCompetitorBlock;
  media: Record<string, BBField>;
  notes: BBField[];
  page_hashes: Record<string, string>;
  /** The brand's own social accounts and its mark; absent on legacy drafts. */
  socials?: BBSocial[];
  logo_url?: string | null;
  /** The confirmed version this draft was minted from; null before the first publish. */
  parent_version?: number | null;
  /** True when the open draft differs from the published version (or carries a user edit
   *  before any publish): the tab shows "Publish changes" off this flag. */
  unpublished?: boolean;
}

export interface BBBuildResponse {
  run_id: string;
  competitor_run_id: string;
}

export interface BBRunEvent {
  stage: string;
  message: string;
  data: Record<string, unknown> | null;
}

export interface BBRunStatus {
  run_id: string;
  status: 'running' | 'done' | 'failed' | 'partial';
  kind: string;
  cost_usd_total: number;
  events: BBRunEvent[];
}

export interface BBChatReply {
  reply: string;
  applied: string[];
  new_rev: number;
  /** false: `reply` is the final text, no narration followed */
  narrate?: boolean;
  /** true: the turn did NOT apply (provider outage) — a following "yes" is not a confirm */
  failed?: boolean;
  /** true: a fix the router could not place — the UI offers the section chips; nothing was stored */
  ask_section?: boolean;
  /** Set when the message asked to add a competitor: who it named ("" when nobody). The chat stores nothing;
   *  the UI opens its add-a-competitor step (the paid comparison) with this filled in. */
  add_competitor?: string | null;
}

/** The four review topics, in walk order. */
export const BB_TOPICS = ['brand', 'audience', 'voice', 'rules'] as const;
export type BBTopicKey = (typeof BB_TOPICS)[number];

/** POST /brands/{id}/brand-brain/suggest — the start form's helper. */
export interface BBSuggestCompetitor {
  name: string;
  website: string;
  instagram_handle: string;
  /** The Instagram profile was checked and exists (deterministic, not AI-asserted). */
  verified: boolean;
}

/** /suggest's verdict on what the typed site IS; anything but 'brand' soft-blocks the form. */
export type BBSiteType = 'brand' | 'marketplace' | 'publisher' | 'social' | 'personal' | 'directory' | 'parked';

export interface BBSuggestResponse {
  website: string;
  /** False when the address is not a hostname or does not resolve — nothing was fetched; `reason` says why. */
  reachable?: boolean;
  reason?: string | null;
  instagram: { handle: string; verified: boolean } | null;
  category: string | null;
  site_type?: BBSiteType | null;
  competitors: BBSuggestCompetitor[];
}
